import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { EmployeeActions } from '../actions/employee/employee.actions';
import { MandateActions } from '../actions/mandate/mandate.actions';

import type { Employee } from '../interfaces/employee/employee.interface';
import type { Mandate } from '../interfaces/client/mandate.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import KvRow from './components/KvRow';
import StageChip from './components/StageChip';

import EmployeeEditModal from './EmployeeEditModal';
import {
  initialsFromName,
  formatDate,
  daysBetween,
  deriveTeam,
  deriveOffice,
  deriveStage,
  deriveProgress,
  deriveCode,
  deriveId,
  type EmployeeTeam
} from './lib/mandate-helpers';

// =============================================================================
//  Employee detail — v3 redesign
//
//  Read-only profile of a single employee.  Mirrors the layout idiom of
//  MandateDetail / ClientDetail so the v3 surfaces feel consistent.
//
//  Sources of data:
//    - employee_store.employees[]   — the headline record + HR fields
//    - mandate_store.mandates[]     — derive "Mandates led" by filtering on
//                                     mandate.lead_id === employee.id
//
//  Stubbed (clearly marked as "Coming soon" tabs until back-end endpoints
//  exist): Performance reviews, Timesheets, Documents — the data is already
//  on the Employee record but loading is per-employee detail-fetch which
//  the v3 list endpoint doesn't provide.
// =============================================================================

const TEAM_TONE: Record<EmployeeTeam, string> = {
  Advisory: 'muted',
  Sponsors: 'info',
  Compliance: 'warn',
  Operations: 'gold'
};

type TabKey = 'overview' | 'mandates' | 'reviews' | 'documents';

const TABS: ReadonlyArray<{ key: TabKey; label: string; ready: boolean }> = [
  { key: 'overview', label: 'Overview', ready: true },
  { key: 'mandates', label: 'Mandates led', ready: true },
  { key: 'reviews', label: 'Performance', ready: false },
  { key: 'documents', label: 'Documents', ready: false }
];

const fullName = (e: Employee): string =>
  `${(e.first_name || '').trim()} ${(e.last_name || '').trim()}`.trim() ||
  e.email ||
  `Employee #${e.id}`;

const EmployeeDetailV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  // Editing a staff record is an HR function on the API side.
  const { isHrPrivileged } = useAuth();

  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];
  // The single-record fetch.  GET /employees no longer carries personal
  // fields (home address, leave balance, emergency contact, birthday) — those
  // live only on GET /employees/:id, which returns them to the employee
  // themselves and to privileged HR roles, and the directory fields to
  // everyone else.
  const detail: Employee | undefined = useAppSelector(
    ({ employee_store }) => employee_store.employee
  );
  const mandates: Mandate[] = useAppSelector(({ mandate_store }) => mandate_store.mandates) || [];

  const [tab, setTab] = useState<TabKey>('overview');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (id) dispatch(EmployeeActions.loadEmployee(id, noOp));
    dispatch(EmployeeActions.loadEmployees(noOp));
    dispatch(MandateActions.loadMandates(noOp));
  }, [dispatch, id]);

  const employee = useMemo(
    () =>
      // Prefer the detail record; fall back to the directory entry so the page
      // still renders its headline fields while the fetch is in flight.
      (detail && String(detail.id) === String(id) ? detail : undefined) ??
      employees.find((e) => String(e.id) === String(id)),
    [detail, employees, id]
  );

  // Derived: mandates where this employee is the named lead.  This stays
  // stable across renders to keep the hook count consistent before the
  // early-return guards below.
  const mandatesLed = useMemo(() => {
    if (!employee) return [] as Mandate[];
    return mandates.filter((m) => String(m.lead_id) === String(employee.id));
  }, [employee, mandates]);

  // Loading / not-found states (preserve hook order — these early returns
  // run only after all hooks above have been called).
  if (!employee && employees.length === 0) {
    return (
      <>
        <Topbar crumbs={['Firm', 'Employees', '…']} />
        <div className="page">
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Loading employee…
          </div>
        </div>
      </>
    );
  }
  if (!employee) {
    return (
      <>
        <Topbar crumbs={['Firm', 'Employees', 'Not found']} />
        <div className="page">
          <button className="back-link" type="button" onClick={() => navigate('/employees')}>
            <Icon name="back" size={14} />
            All employees
          </button>
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Employee <strong>#{id}</strong> not found.
          </div>
        </div>
      </>
    );
  }

  // Derived values --------------------------------------------------------
  const name = fullName(employee);
  const team = deriveTeam(employee);
  const office = deriveOffice(employee);
  const today = new Date();
  // Tenure in days; nullable if no start_date.  Display in years (1dp) when
  // > 1y, else in months / days for clarity.
  const tenureDays = daysBetween(employee.start_date, today);
  const tenureLabel = (() => {
    if (tenureDays == null || tenureDays <= 0) return '—';
    if (tenureDays >= 365) return `${(tenureDays / 365).toFixed(1)} yrs`;
    if (tenureDays >= 60) return `${Math.round(tenureDays / 30)} mo`;
    return `${tenureDays} d`;
  })();

  return (
    <>
      <Topbar
        crumbs={['Firm', 'Employees', name]}
        tools={
          <>
            <button className="btn ghost" type="button" onClick={() => navigate('/employees')}>
              <Icon name="back" size={13} />
              All employees
            </button>
            {isHrPrivileged && (
              <button className="btn primary" type="button" onClick={() => setEditing(true)}>
                <Icon name="check" size={13} />
                Edit
              </button>
            )}
          </>
        }
      />
      {editing && <EmployeeEditModal employee={employee} onClose={() => setEditing(false)} />}
      <div className="page">
        {/* --- Hero card -------------------------------------------------- */}
        <div className="detail-hero">
          <div className="detail-hero-top">
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 6,
                background: 'var(--ink)',
                color: 'var(--gold-soft)',
                display: 'inline-grid',
                placeItems: 'center',
                fontFamily: 'var(--font-display)',
                fontSize: 32,
                fontWeight: 500,
                letterSpacing: 1
              }}
              aria-hidden
            >
              {initialsFromName(name)}
            </div>
            <div className="meta">
              <div className="chips">
                {team && <span className={`chip ${TEAM_TONE[team]}`}>{team}</span>}
                {office && <span className="chip muted">{office}</span>}
                {employee.active_status === 'inactive' && (
                  <span className="chip warn">Inactive</span>
                )}
              </div>
              <h1>{name}</h1>
              <div className="sub">
                {[
                  employee.erole || (employee.role ? employee.role.replace('_', ' ') : undefined),
                  employee.email
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="value">
              <div className="eyebrow">Started</div>
              <div className="v">{formatDate(employee.start_date) || '—'}</div>
              <div className="sub">{tenureLabel} tenure</div>
            </div>
          </div>

          <div className="detail-hero-stats">
            <div className="stat">
              <div className="label">Mandates led</div>
              <div className="value">{mandatesLed.length}</div>
            </div>
            <div className="stat">
              <div className="label">Leave balance</div>
              <div className="value">
                {employee.leave_balance != null ? employee.leave_balance : '—'}
              </div>
            </div>
            <div className="stat">
              <div className="label">Days of leave</div>
              <div className="value">{employee.days_of_leave ?? '—'}</div>
            </div>
            <div className="stat">
              <div className="label">Status</div>
              <div className="value" style={{ textTransform: 'capitalize' }}>
                {employee.status || employee.active_status || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* --- Tabs ------------------------------------------------------- */}
        {/* Clickable stub tabs — non-ready tabs surface a "Coming soon" pane
            below rather than being un-clickable, so users always know what's
            on the page. */}
        <div className="tab-strip">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab ${tab === t.key ? 'active' : ''} ${!t.ready ? 'stub' : ''}`}
              onClick={() => setTab(t.key)}
              title={t.ready ? undefined : 'Coming soon — backend wiring pending'}
            >
              {!t.ready && (
                <span aria-hidden style={{ opacity: 0.4 }}>
                  ·&nbsp;
                </span>
              )}
              {t.label}
            </button>
          ))}
        </div>

        {/* --- Overview --------------------------------------------------- */}
        {tab === 'overview' && (
          <div className="grid detail">
            {/* LEFT column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card flush">
                <div className="card-h">
                  <h3>Recent mandates led</h3>
                  {mandatesLed.length > 0 && (
                    <div className="actions">
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={() => setTab('mandates')}
                      >
                        View all
                        <Icon name="arrow" size={13} />
                      </button>
                    </div>
                  )}
                </div>
                {mandatesLed.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}
                  >
                    No mandates currently led by {employee.first_name || 'this employee'}.
                  </div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Mandate</th>
                        <th>Client</th>
                        <th>Stage</th>
                        <th className="right">Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mandatesLed.slice(0, 5).map((m) => (
                        <tr
                          key={m.id}
                          onClick={() => navigate(`/mandates/${m.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="strong">
                            {deriveCode(m)}
                            <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                              {deriveId(m)}
                            </div>
                          </td>
                          <td>{m.client || '—'}</td>
                          <td>
                            <StageChip stage={deriveStage(m)} />
                          </td>
                          <td className="num right">
                            {deriveProgress(m) === undefined ? (
                              <span className="muted">—</span>
                            ) : (
                              `${deriveProgress(m)}%`
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {(employee.qualifications || employee.home_address) && (
                <div className="card">
                  <h3
                    style={{
                      margin: '0 0 14px 0',
                      fontFamily: 'var(--font-display)',
                      fontSize: 18,
                      fontWeight: 500,
                      color: 'var(--ink)'
                    }}
                  >
                    About
                  </h3>
                  {employee.qualifications && (
                    <KvRow label="Qualifications" value={employee.qualifications} />
                  )}
                  {employee.home_address && (
                    <KvRow label="Home address" value={employee.home_address} />
                  )}
                </div>
              )}
            </div>

            {/* RIGHT column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Contact
                </h3>
                <KvRow label="Email" value={employee.email || '—'} />
                <KvRow label="Mobile" value={employee.mobile_number || '—'} mono />
                <KvRow label="Emergency contact" value={employee.emergency_contact || '—'} />
                <KvRow
                  label="Emergency phone"
                  value={employee.emergency_contact_number || '—'}
                  mono
                />
              </div>

              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Key details
                </h3>
                <KvRow label="Team" value={team} />
                <KvRow label="Office" value={office} />
                <KvRow
                  label="Role"
                  value={employee.erole || (employee.role ? employee.role.replace('_', ' ') : '—')}
                />
                <KvRow label="Started" value={formatDate(employee.start_date) || '—'} mono />
                <KvRow label="Tenure" value={tenureLabel} />
                <KvRow label="Birthday" value={formatDate(employee.birthday) || '—'} mono />
              </div>
            </div>
          </div>
        )}

        {/* --- Mandates led tab ------------------------------------------- */}
        {tab === 'mandates' && (
          <div className="card flush">
            {mandatesLed.length === 0 ? (
              <div className="muted" style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}>
                No mandates currently led by {employee.first_name || 'this employee'}.
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Mandate</th>
                    <th>Client</th>
                    <th>Sector</th>
                    <th>Stage</th>
                    <th className="right">Progress</th>
                    <th>Target close</th>
                  </tr>
                </thead>
                <tbody>
                  {mandatesLed.map((m) => (
                    <tr
                      key={m.id}
                      onClick={() => navigate(`/mandates/${m.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="strong">
                        {deriveCode(m)}
                        <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                          {deriveId(m)}
                        </div>
                      </td>
                      <td>{m.client || '—'}</td>
                      <td>{m.sector || '—'}</td>
                      <td>
                        <StageChip stage={deriveStage(m)} />
                      </td>
                      <td className="num right">
                        {deriveProgress(m) === undefined ? (
                          <span className="muted">—</span>
                        ) : (
                          `${deriveProgress(m)}%`
                        )}
                      </td>
                      <td className="mono">{formatDate(m.expected_end_date) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* --- Coming-soon panes (stubbed tabs) --------------------------- */}
        {tab !== 'overview' && tab !== 'mandates' && (
          <div className="card" style={{ padding: 60, textAlign: 'center' }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Coming soon
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>
              {TABS.find((t) => t.key === tab)?.label}
            </div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
              {tab === 'reviews' && (
                <>
                  Performance reviews need a per-employee fetch endpoint that the v3 list does not
                  provide. The legacy <code>/human-resources/employees/:id</code> page still has
                  this data.
                </>
              )}
              {tab === 'documents' && (
                <>
                  Contracts, ID documents, disciplines and other employment paperwork are stored as
                  Active Storage attachments on the User model. The v3 list endpoint omits them; we
                  need a per-employee fetch to populate this tab.
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeDetailV3;
